/**
 * InvoicePaymentModal — Liquidar ou Baixa Parcial de UMA fatura, pelo billing.
 *
 * Substitui a baixa que ficava no settlement: pagamento começa no billing, que é dono de
 * payment (POST /billing/payments). O caminho é payment → invoice → INVOICE_PAID → settlement
 * movimenta o título.
 *
 * **O modo vem do item de menu que abriu o modal**, não de um campo: Liquidar = liquida o título,
 * Baixa Parcial = mantém aberto, Amortizar Operação = baldinho. Ter os dois (botão E select)
 * permitia abrir "Liquidar" e submeter uma parcial, com o rótulo do botão mentindo.
 *
 * Qual modo cada fatura aceita depende do CENÁRIO do charge, e o menu já filtra: garantia não
 * oferece parcial, e só parcela oferece amortização. Aqui o modo pedido é validado de novo contra
 * o cenário — chegar num modo inválido é bug, não escolha do usuário.
 *
 * Campos de tipo de pagamento e do pagador são os molecules compartilhados com o Registrar
 * Recebimento (components/molecules/payment).
 *
 * TODO(comprovante): upload sem backend ainda — só o nome do arquivo trafega.
 */
import { useContext, useMemo, useState } from 'react';
import { FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { processPayment } from '../services/billingApi';
import { PaymentType } from '../../../../constants/payment';
import PaymentTypeFields, { PaymentTypeValue, emptyPaymentTypeValue } from '../../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../../molecules/payment/PayerFields';
import PaymentDateInput, { toIsoInstant } from '../../../molecules/payment/PaymentDateInput';
import { dueDateHint, amountHint } from '../../../molecules/payment/paymentHints';
import HintBadge from '../../../molecules/payment/HintBadge';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { AuthContext } from '../../../../services/Auth/AuthContext';
import {
    LiquidationMode, MODE_TO_TOTAL_PAYMENT, MODE_LABELS, PAYMENT_MODES_BY_SCENARIO, clampMode,
} from '../constants/liquidationModes';

/** O que o item de menu pediu — mesmo vocabulário do LiquidationMode. */
export type PaymentMode = LiquidationMode;

/** Rótulos do modal por modo: o botão diz exatamente a ação que o usuário escolheu no menu. */
const MODE_UI: Record<LiquidationMode, { titulo: string; button: string; enviando: string }> = {
    TOTAL: { titulo: 'Liquidar fatura', button: 'Liquidar', enviando: 'Liquidando...' },
    PARTIAL: { titulo: 'Baixa parcial', button: 'Baixar Parcial', enviando: 'Baixando...' },
    OPERATION_WATERFALL: { titulo: 'Amortizar operação', button: 'Amortizar', enviando: 'Amortizando...' },
};

const LABEL = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const INPUT = 'w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white';

interface Props {
    invoice: any;
    charge: any;
    /** vem do item do menu que abriu (Liquidar / Baixa Parcial) */
    mode: PaymentMode;
    onClose: () => void;
    onSuccess: () => void;
}

const InvoicePaymentModal: React.FC<Props> = ({ invoice, charge, mode, onClose, onSuccess }) => {
    const auth = useContext(AuthContext);
    const scenario: string = charge.chargeScenario || 'GUARANTEE';
    const allowedModes = PAYMENT_MODES_BY_SCENARIO[scenario] || ['TOTAL'];

    // esperado = o que falta nesta fatura; base das dicas de desconto/acréscimo
    const expected = Number(invoice.amount || 0) - Number(invoice.amountPaid || 0);

    // modo fixo: veio do menu. Clampado porque um modo inválido para o cenário viraria
    // movimentação errada no settlement — melhor cair no permitido do que confiar no chamador.
    const liquidation = clampMode(mode, allowedModes);
    const ui = MODE_UI[liquidation];
    const [amount, setAmount] = useState(expected.toFixed(2));
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [time, setTime] = useState('00:00');
    const [showTime, setShowTime] = useState(false);
    const [notes, setNotes] = useState('');
    const [receiptName, setReceiptName] = useState('');
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    // a fatura já traz o pagador — entra travado, só o documento fica editável (PayerFields)
    const [payer, setPayer] = useState<PayerValue>(() => {
        const p = invoice.payer || {};
        const document = p.documentNumber || p.document_number || '';
        return emptyPayer({
            document,
            lookup: document ? 'found' : 'idle',
            name: p.name || '',
            personId: p.externalId || p.external_id || null,
        });
    });
    const [replaceAction, setReplaceAction] = useState<'CANCEL' | 'UNCOLLECT'>('CANCEL');
    const [submitting, setSubmitting] = useState(false);

    const totalPayment = MODE_TO_TOTAL_PAYMENT[liquidation];
    const isFinancial = typeFields.payment_type === PaymentType.FINANCIAL;

    const dateHint = useMemo(() => dueDateHint(date, invoice.dueDate), [date, invoice.dueDate]);
    // desconto/acréscimo só faz sentido quando o pagamento quita: numa baixa parcial o valor é
    // naturalmente menor que o esperado, sem ser desconto.
    const valueHint = useMemo(
        () => (liquidation === 'TOTAL' ? amountHint(parseFloat(amount), expected) : null),
        [liquidation, amount, expected]);

    /**
     * Espelha a regra usa-original do ProcessPayment. Serve para AVISAR: quando não dá para
     * reusar a fatura, o backend cria uma nova e a original é cancelada/descobrada. Sem esse
     * aviso o usuário vê duas faturas aparecerem sem entender por quê.
     */
    const reusesOriginal = useMemo(() => (
        parseFloat(amount) === Number(invoice.amount)
        && !invoice.boleto
        && totalPayment === (invoice.totalPayment ?? null)
        && typeFields.payment_method !== 'BOLETO'
    ), [amount, invoice.amount, invoice.boleto, invoice.totalPayment, totalPayment, typeFields.payment_method]);

    const handleSubmit = async () => {
        const valor = parseFloat(amount);
        if (!valor || valor <= 0) return toast.error('Informe um valor maior que zero');
        if (typeFields.payment_type === PaymentType.ASSET_DATION && typeFields.asset_dation_list.length === 0) {
            return toast.error('Adicione pelo menos um bem para dação');
        }
        if (isFinancial && !typeFields.receiving_bank_account) {
            return toast.error('Selecione a conta de recebimento');
        }
        // toIsoInstant devolve undefined sem data, e payment_date é obrigatório no backend
        const paymentDate = toIsoInstant(date, time, showTime);
        if (!paymentDate) return toast.error('Informe a data do pagamento');

        setSubmitting(true);
        try {
            await processPayment({
                amount: valor,
                payment_date: paymentDate,
                payment_type: typeFields.payment_type,
                payment_method: isFinancial ? typeFields.payment_method : undefined,
                receiving_bank_account_id: isFinancial ? typeFields.receiving_bank_account : undefined,
                payment_source: typeFields.payment_source,
                is_subrogation: typeFields.is_subrogation,
                pending_guarantor_refund: typeFields.pending_guarantor_refund,
                debt_confession_contract: typeFields.payment_type === PaymentType.DEBT_CONFESSION
                    ? typeFields.debt_confession_contract : undefined,
                asset_dation_list: typeFields.payment_type === PaymentType.ASSET_DATION
                    ? typeFields.asset_dation_list : undefined,
                payer: onlyDigits(payer.document) ? {
                    external_id: payer.personId || undefined,
                    name: payer.name || payer.document,
                    document_number: onlyDigits(payer.document),
                    email: payer.email || undefined,
                    phone: payer.phone || undefined,
                    address_street: payer.address_street || undefined,
                    address_number: payer.address_number || undefined,
                    address_complement: payer.address_complement || undefined,
                    address_neighborhood: payer.address_neighborhood || undefined,
                    address_city: payer.address_city || undefined,
                    address_state: payer.address_state || undefined,
                    address_zip_code: payer.address_zip_code || undefined,
                } : undefined,
                file_name_receipt: receiptName || undefined,
                user_responsible: auth?.user?.username || auth?.user?.email || undefined,
                data: notes ? { notes } : undefined,
                invoices: [{
                    credit_operation_id: charge.creditOperationId,
                    charge_id: charge.chargeId,
                    invoice_id: invoice.invoiceId,
                    value: valor,
                    total_payment: totalPayment,
                    // só têm efeito quando o backend cria fatura nova (senão a original vira PAID)
                    cancel_invoice: !reusesOriginal && replaceAction === 'CANCEL',
                    uncollect: !reusesOriginal && replaceAction === 'UNCOLLECT',
                }],
            });
            toast.success(liquidation === 'TOTAL' ? 'Título liquidado'
                : liquidation === 'OPERATION_WATERFALL' ? 'Operação amortizada' : 'Pagamento registrado');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro ao registrar: ${e.message}`);
        }
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <div>
                        <h3 className="text-lg font-semibold">{ui.titulo}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {charge.contractLabel || charge.contractNumber} · fatura{' '}
                            {String(invoice.invoiceId).slice(0, 8)} · valor {formatBRL(invoice.amount)}
                            {invoice.dueDate && <> · vence {formatDateBR(invoice.dueDate)}</>}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
                    {/* o efeito no título é consequência da ação escolhida no menu — informativo */}
                    <p className="text-sm px-3 py-2 bg-gray-100 dark:bg-slate-700 rounded">
                        Efeito no título: <strong>{MODE_LABELS[liquidation]}</strong>
                        {liquidation === 'OPERATION_WATERFALL' && (
                            <span className="block text-xs text-gray-500 mt-0.5">
                                O valor entra na operação e é distribuído pelas parcelas — não liquida
                                esta parcela diretamente.
                            </span>
                        )}
                        {allowedModes.length === 1 && (
                            <span className="block text-xs text-gray-500 mt-0.5">
                                Recebível de garantia é sempre total — não aceita pagamento parcial.
                            </span>
                        )}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={LABEL}>Data do pagamento</label>
                            <PaymentDateInput
                                date={date} time={time} showTime={showTime} className={INPUT}
                                onChange={next => {
                                    setDate(next.date); setTime(next.time); setShowTime(next.showTime);
                                }}
                            />
                            <HintBadge hint={dateHint} />
                        </div>
                        <div>
                            <label className={LABEL} htmlFor="valor-pago">Valor pago</label>
                            <input id="valor-pago" className={INPUT} value={amount} inputMode="decimal"
                                   placeholder="2.500.000,00"
                                   onChange={e => setAmount(e.target.value.replace(',', '.'))} />
                            {valueHint && <HintBadge hint={valueHint} />}
                            <p className="text-xs text-gray-500 mt-1">
                                Falta nesta fatura: {formatBRL(expected)}
                            </p>
                        </div>
                    </div>

                    <PaymentTypeFields value={typeFields} onChange={setTypeFields} />
                    <PayerFields value={payer} onChange={setPayer} lockWhenFound />

                    <div>
                        <label className={LABEL} htmlFor="obs">Observações</label>
                        <input id="obs" className={INPUT} value={notes}
                               placeholder="Referência bancária, NF, etc."
                               onChange={e => setNotes(e.target.value)} />
                    </div>

                    <div>
                        <label className={LABEL} htmlFor="comprovante">Comprovante</label>
                        <label className="inline-flex items-center gap-2 text-sm px-3 py-2 border dark:border-slate-600 rounded cursor-pointer">
                            <FaCloudUploadAlt /> {receiptName || 'Selecionar arquivo'}
                            <input id="comprovante" type="file" className="hidden"
                                   onChange={e => setReceiptName(e.target.files?.[0]?.name || '')} />
                        </label>
                        <p className="text-xs text-amber-600 mt-1">
                            Upload ainda não implementado — só o nome do arquivo é registrado.
                        </p>
                    </div>

                    {/* Aviso: quando não dá para reusar a fatura, o backend cria uma nova. Sem
                        isso o usuário vê duas faturas surgirem sem entender o motivo. */}
                    {!reusesOriginal && (
                        <div className="px-3 py-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                            <p className="font-medium text-amber-800 dark:text-amber-300">
                                Este pagamento vai criar uma fatura nova
                            </p>
                            <p className="text-amber-700 dark:text-amber-400 mt-0.5">
                                A fatura é imutável quanto à cobrança: valor diferente, boleto emitido ou
                                mudança de efeito no título exigem uma fatura nova. O que fazer com esta:
                            </p>
                            <div className="flex gap-4 mt-2">
                                <label className="inline-flex items-center gap-1">
                                    <input type="radio" name="replace" value="CANCEL"
                                           checked={replaceAction === 'CANCEL'}
                                           onChange={() => setReplaceAction('CANCEL')} />
                                    Cancelar
                                </label>
                                <label className="inline-flex items-center gap-1">
                                    <input type="radio" name="replace" value="UNCOLLECT"
                                           checked={replaceAction === 'UNCOLLECT'}
                                           onChange={() => setReplaceAction('UNCOLLECT')} />
                                    Só deixar de cobrar
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex justify-end gap-2">
                    <button onClick={onClose} disabled={submitting}
                            className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting}
                            className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60">
                        {submitting ? ui.enviando : ui.button}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoicePaymentModal;
